import React, { useState, useContext, useEffect } from 'react';
import config from '../../config';
import { PartnerContext } from '../../contexts/PartnerContext';
import styles from './ProductModal.module.css';

const ProductModal = ({ isOpen, onClose, onSubmit, editingProduct, onProductUpdated }) => {
  const { partner } = useContext(PartnerContext);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [deletedImages, setDeletedImages] = useState([]);
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productBrand, setProductBrand] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productSubCategory, setProductSubCategory] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [productUnit, setProductUnit] = useState('pcs');
  const [productInventory, setProductInventory] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productTags, setProductTags] = useState('');
  const [productImages, setProductImages] = useState([]);
  const [productDiscountedPrice, setProductDiscountedPrice] = useState('');

  useEffect(() => {
    if (editingProduct) {
      setProductName(editingProduct.name || '');
      setProductPrice(editingProduct.price || '');
      setProductBrand(editingProduct.brand || '');
      setProductCategory(editingProduct.category || '');
      setProductSubCategory(editingProduct.subCategory || '');
      setProductQuantity(editingProduct.quantity || '');
      setProductUnit(editingProduct.unit || 'pcs');
      setProductInventory(editingProduct.inventory || '');
      setProductDescription(editingProduct.description || '');
      setProductTags(editingProduct.tags?.join(', ') || '');
      setProductDiscountedPrice(editingProduct.discountedPrice || '');

      const uniqueImages = [...new Set(editingProduct.images || [])].map((image) =>
        image.startsWith('/var/data') ? image.replace('/var/data', '') : image
      );
      setProductImages(uniqueImages);

      let formattedPrimary = editingProduct.primaryImage || null;
      if (formattedPrimary && formattedPrimary.startsWith('/var/data')) {
        formattedPrimary = formattedPrimary.replace('/var/data', '');
      }
      setPrimaryImage(uniqueImages.includes(formattedPrimary) ? formattedPrimary : uniqueImages[0] || null);
    }
  }, [editingProduct]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    const addedCount = files.length;
    const currentCount = productImages.length;

    if (currentCount + addedCount > 5) {
      alert('You can upload a maximum of 5 images in total.');
      return;
    }

    const newFiles = files.filter((file) => !productImages.some((img) => img.name === file.name));
    if (newFiles.length === 0) {
      alert('All selected images are already added.');
      return;
    }

    setProductImages((prevImages) => [...prevImages, ...newFiles]);
  };

  const handleSetPrimaryImage = (image) => {
    setPrimaryImage(image);
  };

  const handleDeleteImage = (index) => {
    setProductImages((prevImages) => {
      const imageToDelete = prevImages[index];
      if (typeof imageToDelete === 'string') {
        setDeletedImages((prev) => [...prev, imageToDelete]);
      }
      const updatedImages = prevImages.filter((_, i) => i !== index);
      if (primaryImage === imageToDelete) {
        setPrimaryImage(updatedImages.length > 0 ? updatedImages[0] : null);
      }
      return updatedImages;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append('name', productName);
    formData.append('price', productPrice);
    formData.append('brand', productBrand);
    formData.append('category', productCategory);
    formData.append('subCategory', productSubCategory);
    formData.append('quantity', productQuantity);
    formData.append('unit', productUnit);
    formData.append('description', productDescription);
    formData.append('tags', productTags);
    formData.append('inventory', productInventory);
    formData.append('discountedPrice', productDiscountedPrice);

    if (partner && partner._id) {
      formData.append('shopId', partner._id);
    } else {
      return;
    }

    const newFiles = productImages.filter((image) => typeof image !== 'string');
    newFiles.forEach((file) => {
      formData.append('images', file);
    });

    if (primaryImage) {
      if (typeof primaryImage === 'string') {
        formData.append('primaryImage', primaryImage);
      } else {
        const newIndex = newFiles.indexOf(primaryImage);
        if (newIndex !== -1) {
          formData.append('primaryImage', `new:${newIndex}`);
        }
      }
    }

    if (deletedImages.length > 0) {
      formData.append('deletedImages', JSON.stringify(deletedImages));
    }

    // Debug: Log FormData
    for (let [key, value] of formData.entries()) {
    }

    try {
      const url = editingProduct ? `${config.backendUrl}/api/products/${editingProduct._id}` : `${config.backendUrl}/api/products`;
      const method = editingProduct ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        if (onProductUpdated) {
          onProductUpdated();
        }
        onClose();
      } else {
        const error = await response.json();
      }
    } catch (error) {
    }
  };

  const resetForm = () => {
    setProductName('');
    setProductPrice('');
    setProductBrand('');
    setProductCategory('');
    setProductSubCategory('');
    setProductQuantity('');
    setProductUnit('pcs');
    setProductInventory('');
    setProductDescription('');
    setProductTags('');
    setProductImages([]);
    setPrimaryImage(null);
    setDeletedImages([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOne}>
      <div className={styles.modalContentOne}>
        <span className={styles.closeModalOne} onClick={resetForm}>
          &times;
        </span>
        <h2 className={styles.addProductH2}>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
        <div className={styles.productImagePreviewContainer}>
          {productImages.map((image, index) => (
            <div key={index} className={styles.imagePreviewItem}>
              <img
                className={styles.productImagePreview}
                src={typeof image === 'string' ? `${config.backendUrl}${image}` : URL.createObjectURL(image)}
                alt={`Product ${index + 1}`}
              />
              <div className={styles.imageBtns}>
                <button
                  type="button"
                  className={styles.setPrimaryImageButton}
                  onClick={() => handleSetPrimaryImage(image)}
                >
                  {image === primaryImage ? 'Primary' : 'Set Primary'}
                </button>
                <button
                  type="button"
                  className={styles.deleteImageButton}
                  onClick={() => handleDeleteImage(index)}
                >
                  &times;
                </button>
              </div>
            </div>
          ))}
        </div>
        <label className={`${styles.productImageLabel} ${styles.productLabel}`}>Images:</label>
        <input
          type="file"
          className={`${styles.productImageInput} ${styles.formInput}`}
          id="productImageInput"
          accept="image/*"
          multiple
          onChange={handleImageChange}
        />
        <form className={styles.productForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={`${styles.productNameLabel} ${styles.productLabel}`}>Name:</label>
            <input
              type="text"
              className={`${styles.productNameInput} ${styles.formInput}`}
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productPriceLabel} ${styles.productLabel}`}>Price:</label>
            <input
              type="number"
              className={`${styles.productPriceInput} ${styles.formInput}`}
              value={productPrice}
              onChange={(e) => setProductPrice(e.target.value)}
              step="0.01"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productPriceLabel} ${styles.productLabel}`}>Discounted Price:</label>
            <input
              type="number"
              className={`${styles.productPriceInput} ${styles.formInput}`}
              value={productDiscountedPrice}
              onChange={(e) => setProductDiscountedPrice(e.target.value)}
              placeholder="e.g., 250"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productBrandLabel} ${styles.productLabel}`}>Brand:</label>
            <input
              type="text"
              className={`${styles.productBrandInput} ${styles.formInput}`}
              value={productBrand}
              onChange={(e) => setProductBrand(e.target.value)}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productCategoryLabel} ${styles.productLabel}`}>Category:</label>

            <select
              id="productCategoryInput"
              className={`${styles.productCategoryInput} ${styles.formInput}`}
              list="productCategories"
              value={productCategory}
              onChange={(e) => setProductCategory(e.target.value)}
              placeholder="Select a category"
              required
            >
              <option value="">Select a category</option>
              <option value="Electronics">Electronics</option>
              <option value="Fashion & Clothing">Fashion & Clothing</option>
              <option value="Home & Kitchen">Home & Kitchen</option>
              <option value="Beauty & Personal Care">Beauty & Personal Care</option>
               <option value="Books & Stationary">Books & Stationary</option>
              <option value="Sports & Outdoors">Sports & Outdoors</option>
              <option value="Food">Food</option>
              <option value="Toys And Games">Toys And Games</option>
              <option value="Health">Health</option>
              <option value="Pet Supplies">Pet Supplies</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productSubcategoryLabel} ${styles.productLabel}`}>Subcategory:</label>
            <input
              type="text"
              className={`${styles.productSubcategoryInput} ${styles.formInput}`}
              value={productSubCategory}
              onChange={(e) => setProductSubCategory(e.target.value)}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productQuantityLabel} ${styles.productLabel}`}>Quantity:</label>
            <input
              type="number"
              className={`${styles.productQuantityInput} ${styles.formInput}`}
              value={productQuantity}
              onChange={(e) => setProductQuantity(e.target.value)}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productUnitLabel} ${styles.productLabel}`}>Unit:</label>
            <select
              className={`${styles.productUnitSelect} ${styles.formInput}`}
              value={productUnit}
              onChange={(e) => setProductUnit(e.target.value)}
              required
            >
              <option value="pcs">Pcs</option>
              <option value="kg">Kg</option>
              <option value="grams">Grams</option>
              <option value="unit">Unit</option>
              <option value="set">Set</option>
              <option value="plate">plate</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productInventoryLabel} ${styles.productLabel}`}>Total Inventory:</label>
            <input
              type="number"
              className={`${styles.productInventoryInput} ${styles.formInput}`}
              value={productInventory}
              onChange={(e) => setProductInventory(e.target.value)}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={`${styles.productTagsLabel} ${styles.productLabel}`}>Tags (comma-separated):</label>
            <input
              type="text"
              className={`${styles.productTagsInput} ${styles.formInput}`}
              value={productTags}
              onChange={(e) => setProductTags(e.target.value)}
            />
          </div>
          <div id="descFormGroup" className={styles.formGroup}>
            <label className={`${styles.productDescriptionLabel} ${styles.productLabel}`}>Description:</label>
            <textarea
              className={`${styles.productDescriptionInput} ${styles.formInput}`}
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              required
            />
          </div>
          <button type="submit" className={styles.submitProductButton}>
            {editingProduct ? 'Update Product' : 'Add Product'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;